import React, { useEffect, useState } from 'react';
import { Loader2, Play, RefreshCw } from 'lucide-react';
import api from '@/api/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('pt-PT');
  } catch {
    return value;
  }
};

export default function AutoImportView() {
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [configRes, logsRes] = await Promise.all([
        api.get('/auto-import/config'),
        api.get('/auto-import/logs', { params: { limit: 20 } }).catch(() => ({ data: { data: [] } })),
      ]);
      setConfig(configRes.data?.data || null);
      setLogs(logsRes.data?.data || []);
    } catch {
      toast.error('Erro ao carregar configuração de auto-import');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const run = async () => {
    setRunning(true);
    try {
      await api.post('/auto-import/run');
      toast.success('Importação automática executada');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao executar importação');
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" />
        <p className="mt-3 text-sm text-muted-foreground">Carregando Importações Automáticas...</p>
      </div>
    );
  }

  const providers = config?.providers || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Importações Automáticas</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading || running}>
            <RefreshCw size={16} className="mr-2" /> Atualizar
          </Button>
          <Button onClick={run} disabled={running}>
            {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play size={16} className="mr-2" />}
            Executar Importação
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Configuração</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Ativo: <span className="font-medium">{config?.auto_import_enabled ? 'Sim' : 'Não'}</span>
          </p>
          <div className="grid gap-4 md:grid-cols-3">
            {providers.map((p) => (
              <Card key={p.provider}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base capitalize">{p.provider}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Configurado</span>
                    <span className="font-medium">{p.credentials?.is_active ? 'Sim' : 'Não'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Ativo</span>
                    <span className="font-medium">{p.config?.enabled ? 'Sim' : 'Não'}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">Última sync: {formatDate(p.credentials?.last_sync_at)}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Logs Recentes</CardTitle>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem logs de importação automática.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2">Provider</th>
                    <th>Data</th>
                    <th>Status</th>
                    <th className="text-right">Importados</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} className="border-b">
                      <td className="py-2 capitalize">{log.provider}</td>
                      <td>{formatDate(log.started_at)}</td>
                      <td>{log.status || '—'}</td>
                      <td className="text-right">{log.trips_imported ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
